import type { PostHogFeatureFlag } from '@/config/availableFeatureFlags';
import { useAuth } from '@/contexts/auth';
import { DashboardView } from '@/domain/auth';
import { usePostHogFeatureFlag } from '@/hooks/usePostHogFeatureFlag';

/** The flag the fish group board sits behind. Without it, a saved board default falls back. */
export const FISH_GROUPS_VIEW_FLAG: PostHogFeatureFlag = 'fish-groups-client-management';

/**
 * The view a user lands on: the saved one, except that the fish group board needs its flag.
 * Without the flag (or before PostHog answers) it is the biological view, the normal default.
 */
export function effectiveDashboardView(
    saved: DashboardView | undefined,
    fishGroupsEnabled: boolean
): DashboardView {
    if (saved === DashboardView.operational) return DashboardView.operational;
    if (saved === DashboardView.fishGroups && fishGroupsEnabled) return DashboardView.fishGroups;
    return DashboardView.biological;
}

/** Where each view lives under a location's base path. */
export function locationDashboardPath(basePath: string, view: DashboardView): string {
    switch (view) {
        case DashboardView.operational:
            return `${basePath}/operational-dashboard`;
        case DashboardView.fishGroups:
            return `${basePath}/fish-groups`;
        default:
            return basePath;
    }
}

/** The signed-in user's default location view, with the flag applied. */
export function usePreferredDashboardView(): DashboardView {
    const { user } = useAuth();
    const { enabled } = usePostHogFeatureFlag(FISH_GROUPS_VIEW_FLAG);
    return effectiveDashboardView(user?.profile?.defaultLocationDashboard, enabled);
}

/** The URL that opens a location on the user's default view. */
export function useLocationDashboardUrl(clientId: number, locationId: number): string {
    const view = usePreferredDashboardView();
    return locationDashboardPath(`/c/${clientId}/location/${locationId}`, view);
}
